import * as React from "react"

import { cn } from "@/lib/utils"

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        // `text-xs` (11,25 px) y no `text-sm` (13,1): las celdas del diseño
        // miden 11,5. `text-sm` no es ningún nivel de la rampa del DS —cae
        // entre Caption (12) y Body (15)—, así que toda tabla salía un 14 %
        // más grande que el diseño sin que nadie lo hubiera elegido.
        //
        // La t-shirt size y NO `text-caption`, que mide lo mismo: los tokens
        // propios de texto (`text-caption`, `text-label`, `text-h2`…) los
        // clasifica tailwind-merge como COLOR, así que acá adentro de un `cn`
        // un consumidor que pasara `className="text-ink-muted"` tumbaría el
        // tamaño en silencio. `text-xs` sí lo reconoce como tamaño.
        //
        // El peso y el color de cada celda los sigue poniendo cada tabla:
        // esta es la base, no el estilo.
        className={cn("w-full caption-bottom text-xs", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
        className
      )}
      {...props}
    />
  )
}

/**
 * Encabezados en VERSALITAS: 10 px, peso 800 y un tracking abierto.
 *
 * En una tabla de seis columnas el encabezado tiene que pesar MENOS que el
 * dato, o compite con el. Es lo que hace el diseno en sus tablas anchas, y va
 * como variante y no escrita en cada pantalla porque estaba en tres lugares
 * con tres recetas distintas —dos tamanos y dos colores— para una sola cosa.
 *
 * Sale del encabezado de "Enlazar referencias", que es donde se midio.
 */
const ENCABEZADO_EN_VERSALITAS =
  "text-ink-hint text-[0.625rem] font-extrabold tracking-[0.06em] uppercase"

function TableHead({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"th"> & { variant?: "default" | "versalitas" }) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        // `font-bold` (700) y no `font-medium` (500): las tres tablas del
        // diseno que se midieron —referencias, informes y usuarios— ponen sus
        // `<th>` en 700 sin excepcion. Es el unico peso de tabla que el diseno
        // fija igual en todas, asi que este SI es base y no eleccion de cada
        // pantalla.
        "text-foreground h-10 px-2 text-left align-middle font-bold whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        variant === "versalitas" && ENCABEZADO_EN_VERSALITAS,
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
